/**
 * The panes and units of the chosen configuration, on the sheet and in the side panel
 * (ADR-0020). Panes mode is for fixing rectangles: their boxes, kinds and tags. Units
 * mode is for grouping panes into the units that are built, and naming them.
 */
import { useEffect, useState } from "react";

import type { WorkspaceConfig, WorkspaceOp, WorkspacePane } from "@/api/client";
import { chosenUnit, feetInches, parseTags } from "@/flow/workspace";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Kbd } from "@/ui/primitives";
import type { SheetGeometry } from "@/viewer/SheetCanvas";

export type Mode = "configs" | "panes" | "units";

export const PANE_KEYS: readonly [string, string][] = [
  ["Tab", "Next pane (Shift: previous)"],
  ["N", "Draw a pane"],
  ["B", "Redraw this pane's box"],
  ["T", "Tag it (GL-1, MP-1)"],
  ["D / V", "Door or vent, or back to a pane"],
  ["Del", "Delete it"],
  ["Z", "Undo"],
  ["Esc", "Back to the configurations"],
];

export const UNIT_KEYS: readonly [string, string][] = [
  ["Click", "Choose a unit (Shift: add or take away a pane)"],
  ["G", "Make the chosen panes one unit"],
  ["X", "Make each chosen pane a unit"],
  ["H", "Split units at the pointer"],
  ["M", "Name the chosen unit"],
  ["R", "Back to the proposed units"],
  ["Esc", "Back to the configurations"],
];

const KIND_COLOUR: Record<string, string> = {
  pane: "var(--color-accent)",
  leaf: "var(--color-ok)",
  door: "var(--color-violet)",
  shape: "var(--color-warn)",
};

function kindName(p: WorkspacePane): string {
  if (p.kind === "leaf") return "Vent";
  if (p.kind === "door") return "Door";
  if (p.kind === "shape") return "Shape";
  return p.glass ? "Glass" : "Panel";
}

/** The chosen configuration's panes, or its units, drawn over the sheet. */
export function PaneLayer({
  geometry,
  config,
  mode,
  pane,
  chosen,
}: {
  geometry: SheetGeometry;
  config: WorkspaceConfig;
  mode: Mode;
  pane: string | null;
  chosen: readonly string[];
}) {
  const font = geometry.viewWidth / 140;
  return (
    <g data-layer={mode}>
      {config.panes.map((p) => {
        const r = geometry.px(p.bbox_norm);
        const on = mode === "panes" ? p.id === pane : chosen.includes(p.id);
        return (
          <g key={p.id} data-pane={p.id} data-on={on || undefined}>
            <rect
              {...r}
              fill={KIND_COLOUR[p.kind] ?? KIND_COLOUR.pane}
              fillOpacity={on ? 0.28 : 0.06}
              stroke={KIND_COLOUR[p.kind] ?? KIND_COLOUR.pane}
              strokeWidth={on ? 2.5 : 1}
              vectorEffect="non-scaling-stroke"
            />
            {mode === "panes" && p.tags.length ? (
              <text x={r.x + 2} y={r.y + font * 1.1} fontSize={font} fill="var(--color-ink)">
                {p.tags.join(" ")}
              </text>
            ) : null}
          </g>
        );
      })}
      {mode === "units"
        ? config.units.map((u) => {
            const r = geometry.px(u.bbox_norm);
            return (
              <g key={u.key} data-unit={u.key}>
                <rect
                  {...r}
                  fill="none"
                  stroke="var(--color-violet)"
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={r.x + 2}
                  y={r.y + r.height - font * 0.4}
                  fontSize={font}
                  fontWeight={600}
                  fill="var(--color-violet)"
                >
                  {u.name}
                </text>
              </g>
            );
          })
        : null}
    </g>
  );
}

export function PanesPanel({
  config,
  pane,
  editingTags,
  canEdit,
  onPick,
  onOps,
  onDone,
}: {
  config: WorkspaceConfig;
  pane: string | null;
  editingTags: boolean;
  canEdit: boolean;
  onPick: (id: string) => void;
  onOps: (ops: WorkspaceOp[]) => void;
  onDone: () => void;
}) {
  const chosen = config.panes.find((p) => p.id === pane) ?? null;
  const [tags, setTags] = useState("");
  useEffect(() => setTags(chosen?.tags.join(", ") ?? ""), [chosen?.id, chosen?.tags]);
  const flagged = new Set(config.flags.map((f) => f.pane_id));
  return (
    <section aria-label="Panes" className="space-y-2">
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Panes ({config.panes.length})
      </h3>
      {editingTags && chosen && canEdit ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onOps([{ op: "edit_pane", pane_id: chosen.id, tags: parseTags(tags) }]);
            onDone();
          }}
        >
          <input
            autoFocus
            aria-label="Tags"
            className="w-full rounded-md border border-line-strong bg-surface px-2 py-1 text-sm"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            onBlur={onDone}
            onKeyDown={(e) => e.key === "Escape" && onDone()}
          />
        </form>
      ) : null}
      <ul className="divide-y divide-line rounded-md border border-line">
        {config.panes.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onPick(p.id)}
              aria-current={p.id === pane ? "true" : undefined}
              className={cn(
                "flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm",
                p.id === pane ? "bg-accent-soft" : "hover:bg-sunken",
              )}
            >
              <span>
                {kindName(p)}{" "}
                {p.tags.length ? <span className="text-ink-2">{p.tags.join(", ")}</span> : null}
              </span>
              {flagged.has(p.id) ? <Badge tone="warn">check</Badge> : null}
              {!p.tags.length && !flagged.has(p.id) && p.notes.includes("strip") ? (
                <span className="text-xs text-muted">strip</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function UnitsPanel({
  config,
  chosen,
  naming,
  canEdit,
  onChoose,
  onOps,
  onDone,
}: {
  config: WorkspaceConfig;
  chosen: readonly string[];
  naming: boolean;
  canEdit: boolean;
  onChoose: (paneIds: string[]) => void;
  onOps: (ops: WorkspaceOp[]) => void;
  onDone: () => void;
}) {
  const unit = chosenUnit(config, chosen);
  const [name, setName] = useState("");
  useEffect(() => setName(unit?.name ?? ""), [unit?.key, unit?.name]);
  return (
    <section aria-label="Units" className="space-y-2">
      <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Units ({config.units.length})
      </h3>
      {naming && unit && canEdit ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onOps([
              {
                op: "name_unit",
                configuration_id: config.id,
                pane_ids: unit.pane_ids,
                name: name.trim() || null,
              },
            ]);
            onDone();
          }}
        >
          <input
            autoFocus
            aria-label="Unit name"
            placeholder={unit.proposed_name}
            className="w-full rounded-md border border-line-strong bg-surface px-2 py-1 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={onDone}
            onKeyDown={(e) => e.key === "Escape" && onDone()}
          />
        </form>
      ) : null}
      <ul className="divide-y divide-line rounded-md border border-line">
        {config.units.map((u) => (
          <li key={u.key}>
            <button
              type="button"
              onClick={() => onChoose([...u.pane_ids])}
              aria-current={u.key === unit?.key ? "true" : undefined}
              className={cn(
                "flex w-full items-baseline justify-between gap-2 px-2 py-1.5 text-left text-sm",
                u.key === unit?.key ? "bg-accent-soft" : "hover:bg-sunken",
              )}
            >
              <span className={cn("font-medium", u.edited ? "text-ink" : "text-ink-2")}>
                {u.name}
                {u.qty > 1 ? <span className="text-muted"> ×{u.qty}</span> : null}
                {u.edited ? (
                  <span className="ml-1 text-xs font-normal text-muted">was {u.proposed_name}</span>
                ) : null}
              </span>
              <span className="text-xs text-muted">
                {u.pane_ids.length} {u.pane_ids.length === 1 ? "pane" : "panes"} ·{" "}
                {feetInches(u.width_in)} × {feetInches(u.height_in)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {canEdit && chosen.length ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() =>
              onOps([{ op: "group_units", configuration_id: config.id, pane_ids: [...chosen] }])
            }
          >
            One unit <Kbd>G</Kbd>
          </Button>
          <Button
            size="sm"
            onClick={() =>
              onOps([{ op: "split_unit", configuration_id: config.id, pane_ids: [...chosen] }])
            }
          >
            Split <Kbd>X</Kbd>
          </Button>
        </div>
      ) : null}
    </section>
  );
}
